/**
 * @description Converts a record of comma-separated string values to a record of arrays of strings. This is useful when you want to
 * convert the query params from the URL into an object with arrays of strings.
 * @param obj - the object with comma-separated values
 * @param excludeKeys - the keys that should be excluded from the conversion. sometimes you might have a form element that
 * has a comma-separated value, but you don't want to convert it to an array of strings. In this case, you can pass the key
 * to this parameter.
 *
 * @example
 * const myObject = {
 *   key1: 'value1,value2',
 *   key2: 'value3,value4',
 *   key3: 'value5',
 * };
 *
 * const convertedObject = convertCommaSeparatedValues(myObject);
 * // returns
 * // {
 * //   key1: ['value1', 'value2'],
 * //   key2: ['value3', 'value4'],
 * //   key3: 'value5',
 * // }
 *
 * // if you want to exclude a key from the conversion, you can do it like this:
 * const convertedObject = convertCommaSeparatedValues(myObject, ['key2']);
 * // returns
 * // {
 * //   key1: ['value1', 'value2'],
 * //   key2: 'value3,value4',
 * //   key3: 'value5',
 */
export const convertCommaSeparatedValues = <T extends object, K extends keyof T>(obj: T, excludeKeys?: string[]): T => {
  const newObj = {} as T;

  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === 'string' && value.includes(',') && !(excludeKeys && excludeKeys.find((excluded) => excluded === key))) {
      newObj[key as K] = value.split(',') as T[K];
    } else {
      newObj[key as K] = value as T[K];
    }
  });

  return newObj;
};
